<script setup lang="ts">
import { computed, provide } from 'vue';
import ColumnMapping from '@/components/ColumnMapping.vue';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { buildEntityColumns } from '@/composables/entityColumns';
import { inspectorComponentFor, inspectorExcludeFieldsFor } from '@/composables/searchInspectorRegistry';
import { RECORD_PAGE_CONTEXT } from '@/composables/recordPageContext';
import { PEEK_ORIGIN_TITLE } from '@/composables/peekOrigin';
import { applyLayout, chipLabelsOf, type RecordLayoutDefinition } from '@/composables/recordLayout/recordLayoutDefinition';
import { recordHeadingText } from '@/composables/recordLayout/recordHeading';
import { recordNounFor } from '@/composables/recordLayout/useRecordHeading';

/**
 * A record's body, the same on its own page and in the side peek: what it is
 * and where it sits, its name, the pinned field chips, and its sections, all
 * drawn from the person's layout. The page and the peek wrap it in their own
 * header.
 *
 * With labels hidden, a chip shows only its value, and hovering it names the
 * field.
 *
 * The top line and the title come from the layout, {@see recordHeadingText}.
 * In the side peek and the list's pane, the host's header draws
 * the same two rows from the same layout, so the compact body starts at the
 * chips rather than naming the record twice.
 */
const props = withDefaults(
    defineProps<{
        entityId: string;
        recordId: number | string;
        row: Record<string, unknown>;
        layout: RecordLayoutDefinition;
        initialSection?: string | null;
        /** The side peek's narrower, smaller version. */
        compact?: boolean;
        /** The page keeps the open tab in its address. The peek does not. */
        onSectionChange?: (section: string) => void;
    }>(),
    { initialSection: null, compact: false, onSectionChange: undefined },
);

const columns = computed(() => buildEntityColumns(props.entityId));
const detailComponent = computed(() => inspectorComponentFor(props.entityId));
const excludeFields = computed(() => inspectorExcludeFieldsFor(props.entityId));
const noun = computed(() => recordNounFor(props.entityId));

// The top line and the title, from the layout. The peeks draw the same words in their own header.
const heading = computed(() => recordHeadingText(props.layout, columns.value, props.row, noun.value, props.recordId));
const headerColumns = computed(() => applyLayout(props.layout, columns.value, { entityId: props.entityId }).headerColumns);
const hideLabels = computed(() => chipLabelsOf(props.layout) === 'hide');

// A related link in this body opens a peek whose trail starts with this record's name.
provide(PEEK_ORIGIN_TITLE, computed(() => heading.value.title));

provide(RECORD_PAGE_CONTEXT, {
    onSectionChange: (section: string) => props.onSectionChange?.(section),
    groupsFor: (pageColumns, pageExcludeFields) => applyLayout(props.layout, pageColumns, { excludeFields: pageExcludeFields, entityId: props.entityId }).groups,
});
</script>

<template>
  <div class="flex flex-col" :class="compact ? 'gap-4' : 'gap-5'" data-testid="record-body">
    <div v-if="!compact" class="flex flex-col gap-1.5">
      <p v-if="heading.topLine" class="text-sm text-muted-foreground" data-testid="record-place-line">{{ heading.topLine }}</p>
      <h1 class="text-3xl font-semibold tracking-tight" data-testid="record-title">{{ heading.title }}</h1>
    </div>

    <!-- Chips show their whole value, on one line. The value's own renderer, such
         as a related record's link, truncates in a table cell, so that is undone
         here. A chip grows to fit its value, up to the width of the row. Only a
         value longer than the whole row is cut, at the row's edge. -->
    <!-- `-mx-3` matches each chip's `px-3`, so a chip's text starts on the same
         edge as the title and the fields below. The rounded fill hangs into the
         page margin instead. Without it the chips looked shifted right. -->
    <ul v-if="headerColumns.length > 0" class="-mx-3 flex flex-wrap gap-2" aria-label="Key fields" data-testid="record-chips">
      <li v-for="column in headerColumns" :key="column.id" class="max-w-full" :data-testid="`record-chip-${column.id}`">
        <Tooltip v-if="hideLabels">
          <TooltipTrigger as-child>
            <span tabindex="0" :aria-label="column.header" class="inline-flex h-7 max-w-full items-center overflow-hidden rounded-full bg-muted px-3 text-xs [&_a]:no-underline">
              <span class="min-w-0 text-foreground whitespace-nowrap [&_*]:whitespace-nowrap [&_.truncate]:overflow-visible [&_.truncate]:text-clip" data-testid="record-chip-value"><ColumnMapping :field="column.key" :data-type="column.dataType" :data="row" /></span>
            </span>
          </TooltipTrigger>
          <TooltipContent data-testid="record-chip-tooltip">{{ column.header }}</TooltipContent>
        </Tooltip>
        <span v-else class="inline-flex h-7 max-w-full items-center gap-1.5 overflow-hidden rounded-full bg-muted px-3 text-xs [&_a]:no-underline">
          <span class="shrink-0 text-muted-foreground" data-testid="record-chip-label">{{ column.header }}</span>
          <span class="min-w-0 text-foreground whitespace-nowrap [&_*]:whitespace-nowrap [&_.truncate]:overflow-visible [&_.truncate]:text-clip" data-testid="record-chip-value"><ColumnMapping :field="column.key" :data-type="column.dataType" :data="row" /></span>
        </span>
      </li>
    </ul>

    <div class="@container">
      <component
        :is="detailComponent"
        :row="row"
        :columns="columns"
        :exclude-fields="excludeFields"
        :entity-id="entityId"
        :initial-section="initialSection"
      />
    </div>
  </div>
</template>
